import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AssessmentRunOption } from '../../shared/utils';
import { baseBmpPerformanceFormSchema, type EditBmpPerformanceFormData } from '../BmpPerformanceFormConfig';
import BmpPerformanceInnerForm from '../BmpPerformanceInnerForm';
import type { BmpPerformanceRowData } from '../BmpPerformanceTab';
import {
    UpdatePerformanceBmpResultsDocument,
    type UpdatePerformanceBmpResultsMutation,
} from './EditPerformanceBmpResult.api';
import client from '@/apolloClient';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { useGetCompanyId } from '@/contexts/UserSession';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import '@/utils/notifications';

interface EditPerformanceBmpModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    rowData: BmpPerformanceRowData | null;
    assessmentRunOptions: AssessmentRunOption[];
}

const EditPerformanceBmpModal = ({ open, onClose, rowData, assessmentRunOptions }: EditPerformanceBmpModalProps) => {
    const { t } = useTranslation('performanceResult');
    const selectedCompanyId = useGetCompanyId();

    const form = useForm<EditBmpPerformanceFormData>({
        mode: 'onChange',
        resolver: zodResolver(baseBmpPerformanceFormSchema),
        defaultValues: {
            percentage: rowData?.percentage ?? undefined,
            date: rowData?.assessmentDate ?? undefined,
            isTiedToAssessmentRun: rowData?.isTiedToAssessmentRun ?? false,
            assessmentRunId: rowData?.assessmentRunId ?? undefined,
        },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    const handleSubmit = async () => {
        if (!(await form.trigger()) || !rowData) {
            return;
        }
        const values = form.getValues();

        try {
            const { data } = await client.mutate<UpdatePerformanceBmpResultsMutation>({
                mutation: UpdatePerformanceBmpResultsDocument,
                variables: {
                    id: rowData.id,
                    input: {
                        companyId: selectedCompanyId || '',
                        assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId || undefined : undefined,
                        result: {
                            percentage: values.percentage ?? undefined,
                        },
                        completedAt: values.date ?? undefined,
                    },
                },
            });

            if (data?.updateBmpResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.updateBmpResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('updateError.description', { type: 'BMP' });

                showErrorNotification({
                    message: t('updateError.title', { type: 'BMP' }),
                    description: errorMessage,
                });

                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('updateSuccess.title', { type: 'BMP' }),
                    description: t('updateSuccess.message', { type: 'BMP' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error updating BMP performance result:', error);
            throw error;
        }
    };

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button outline onClick={() => onClose()}>
                    {t('bmp.editModal.cancelBtn')}
                </Button>
                <Button color="teal" onClick={handleSubmit}>
                    {t('bmp.editModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('bmp.editModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('bmp.form.date')} required />
                        </div>
                        <BmpPerformanceInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default EditPerformanceBmpModal;
